import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid/Grid";
import useEnv from "../../../hooks/env";
import CustomButton from "../../../components/Button/CustomButton";
import {
  BaseProductsSearchAutocomplete,
  NartsSearchAutocomplete,
  ProductNamesSearchAutocomplete,
} from "../../../components/Autocomplete/CustomSearchAutoComplete.instances";
import ProductApi from "../../../api/services/ProductApi";
import NartResponse from "../../../api/models/product/NartResponse";
import {
  MIN_NART_CHARACTERS,
  ProductSuggestionInnerFormikProps,
} from "../DescriptionTab/models/ProductSuggestionForm/SuggestionForm";
import BaseProductResponse from "../../../api/models/product/BaseProductResponse";
import ProductNameResponse from "../../../api/models/product/ProductNameResponse";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    "& > * + *": {
      marginTop: theme.spacing(4),
    },
  },
  nartSearchHelperText: {
    lineHeight: "12px",
    marginBottom: "4px",
    fontWeight: 400,
    fontSize: "0.75rem",
    fontFamily: "Proxima Nova",
    color: "rgb(117, 117, 117)",
  },
}));

const initialFormValues = {
  nartField: { nart: "" },
  productNameField: { code: "", description: "" },
  baseProductField: { code: "", description: "" },
};

interface ProductSampleAddFormProps {
  handleFormChange?: (values: ProductSuggestionInnerFormikProps) => void;
  handleSubmit?: (values: ProductSuggestionInnerFormikProps) => void;
  oneRow?: boolean;
  hideSubmit?: boolean;
}

const ProductSampleAddForm = (props: ProductSampleAddFormProps) => {
  const classes = useStyles();

  const { api } = useEnv();
  const productApi = new ProductApi(api);

  const { hideSubmit, handleFormChange, handleSubmit, oneRow } = props;

  const formRef = useRef<FormikProps<ProductSuggestionInnerFormikProps>>(null);
  const nartSearch = useRef<HTMLDivElement & { reset: () => void }>(null);

  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);

  const [disabledNartField, setDisabledNartField] = useState(false);
  const [productNameFieldChanged, setProductNameFieldChanged] = useState(false);
  const [baseProductFieldChanged, setBaseProductFieldChanged] = useState(false);

  useEffect(() => {
    const nartValue = formRef?.current?.values.nartField.nart;
    if (nartValue && disabledNartField) {
      productApi
        .getProductWithPropsByNart(nartValue)
        .then(({ data }) => {
          showSnackbar("Product properties received", "success");
          formRef.current?.setFieldValue("productNameField", {
            code: data.productName!,
            description: data.productNameDescription!,
          });
          formRef.current?.setFieldValue("baseProductField", {
            code: data.baseProduct!,
            description: data.baseProductDescription!,
          });
        })
        .catch(handleHttpError);
    }
  }, [disabledNartField]);

  useEffect(() => {
    const filterCode = formRef.current?.values.productNameField.code;
    formRef.current?.setFieldValue("nartField", {
      nart: filterCode,
    });
  }, [productNameFieldChanged]);

  useEffect(() => {
    const filterCode = formRef.current?.values.baseProductField.code;
    formRef.current?.setFieldValue("nartField", {
      nart: filterCode,
    });
  }, [baseProductFieldChanged]);

  return (
    <Formik<ProductSuggestionInnerFormikProps>
      innerRef={formRef}
      initialValues={initialFormValues}
      onSubmit={(values) => {
        if (handleSubmit) {
          handleSubmit(values);
        }
      }}
    >
      {(formikProps: FormikProps<ProductSuggestionInnerFormikProps>): JSX.Element => {
        const { isSubmitting, values, setFieldValue } = formikProps;

        useEffect(() => {
          if (handleFormChange) {
            handleFormChange(values);
          }
        }, [values]);

        return (
          <Form className={classes.container}>
            <Grid container justify="flex-start" direction="row" spacing={4}>
              <Grid item xs={oneRow ? 6 : 12}>
                <span className={classes.nartSearchHelperText}>
                  Enter a NART that is at least 3 characters long to perform a search.
                </span>
                <NartsSearchAutocomplete
                  autoFocus
                  mandatory
                  ref={nartSearch}
                  inputLabel="NART"
                  onChange={(e, value): void => {
                    if (value) {
                      setFieldValue("nartField", value);
                      setDisabledNartField(true);
                    } else {
                      setFieldValue("nartField", initialFormValues.nartField);
                      setDisabledNartField(false);
                    }
                  }}
                  optionFetch={(searchTerm): Promise<{ totalPages: number; data: NartResponse[] }> => {
                    if (searchTerm.length > MIN_NART_CHARACTERS) {
                      return productApi.getNarts(searchTerm).then((response) => ({
                        totalPages: 1,
                        data: response.data,
                      }));
                    }
                    return Promise.resolve({
                      totalPages: 1,
                      data: [],
                    });
                  }}
                  map={(value): { key: string; label: string } => ({
                    key: value.nart,
                    label: value.nart,
                  })}
                  value={values.nartField}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <ProductNamesSearchAutocomplete
                disabled={Boolean(values.productNameField.code) || baseProductFieldChanged}
                inputLabel="Product name"
                onChange={(e, value): void => {
                  if (value) {
                    setFieldValue("productNameField", value);
                    setProductNameFieldChanged(true);
                  } else {
                    setFieldValue("productNameField", initialFormValues.productNameField);
                  }
                }}
                optionFetch={(searchTerm): Promise<{ totalPages: number; data: ProductNameResponse[] }> => {
                  return productApi.getProductNames(searchTerm).then((response) => ({
                    totalPages: 1,
                    data: response.data,
                  }));
                }}
                map={(value): { key: string; label: string } => ({
                  key: value.code,
                  label: value.code,
                })}
                value={values.productNameField}
              />
            </Grid>
            <Grid item xs={12}>
              <BaseProductsSearchAutocomplete
                disabled={Boolean(values.baseProductField.code) || productNameFieldChanged}
                inputLabel="Base product"
                onChange={(e, value): void => {
                  if (value) {
                    setFieldValue("baseProductField", value);
                    setBaseProductFieldChanged(true);
                  } else {
                    setFieldValue("baseProductField", initialFormValues.baseProductField);
                  }
                }}
                optionFetch={(searchTerm): Promise<{ totalPages: number; data: BaseProductResponse[] }> => {
                  return productApi.getBaseProducts(searchTerm).then((response) => ({
                    totalPages: 1,
                    data: response.data,
                  }));
                }}
                map={(value): { key: string; label: string } => ({
                  key: value.code,
                  label: value.code,
                })}
                value={values.baseProductField}
              />
            </Grid>
            {!hideSubmit && (
              <Grid container item direction="row" justify="flex-end">
                <CustomButton
                  disabled={isSubmitting || !values.nartField.nart}
                  primary
                  text="Use Product"
                  type="submit"
                />
              </Grid>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default ProductSampleAddForm;
